import { useState, useEffect } from 'react';
import { intervalToDuration } from 'date-fns';
import { useInterval } from 'react-use';

const useCountdown = (targetDate: Date | null): string | null => {
  const [countdownString, setCountdownString] = useState<string | null>(null);

  const calculateCountdown = (target: Date): string | null => {
    const now = new Date();

    if (target <= now) {
      return null;
    }

    const duration = intervalToDuration({ start: now, end: target });

    // format as "00d 00h 00m 00s"
    const parts = [];

    // only include days if there are any
    if (duration.days && duration.days > 0) {
      parts.push(`${duration.days.toString().padStart(2, '0')}d`);
    }

    // always show hours, minutes, and seconds
    parts.push(`${(duration.hours ?? 0).toString().padStart(2, '0')}h`);
    parts.push(`${(duration.minutes ?? 0).toString().padStart(2, '0')}m`);
    parts.push(`${(duration.seconds ?? 0).toString().padStart(2, '0')}s`);

    return parts.join(' ');
  };

  // calculate initial value immediately
  useEffect(() => {
    if (!targetDate) {
      setCountdownString(null);
      return;
    }

    setCountdownString(calculateCountdown(targetDate));
  }, [targetDate]);

  useInterval(
    () => {
      if (!targetDate) {
        setCountdownString(null);
        return;
      }

      setCountdownString(calculateCountdown(targetDate));
    },
    targetDate ? 1000 : null
  );

  return countdownString;
};

export default useCountdown;
